import React from 'react';
import PropTypes from 'prop-types';
import Checkbox from '../../Checkbox';
import ToggleSwitch from '../../ToggleSwitch';
import Radio from '../../Radio';
import Text from '../../Text';
import Collapse from '../../Collapse';
import Divider from '../../Divider';
import { dataHooks, TYPES } from '../constants';
import { isString } from '../../utils/StringUtils';
import { st, classes } from './Item.st.css';
import { WixStyleReactContext } from '../../WixStyleReactProvider/context';

export default class SelectableAccordionItem extends React.PureComponent {
  static propTypes = {
    /** A title of the item */
    title: PropTypes.node,

    /** An optional second row of the header */
    subtitle: PropTypes.node,

    /** A content of the item */
    content: PropTypes.node,

    /** A type can be ether radio, checkbox, or toggle, which will effect the way an accordion item is selected */
    type: PropTypes.oneOf(['radio', 'checkbox', 'toggle']),

    /** A flag that indicates a open state */
    open: PropTypes.bool,

    /** An index of the item in the items list */
    idx: PropTypes.number,

    /** A callback which is invoked every time the selection of the item is changed */
    onChange: PropTypes.func,

    /** Extra space on top and bottom of selectable accordion item */
    verticalPadding: PropTypes.oneOf(['medium', 'small', 'tiny']),

    /** Is item disabled */
    disabled: PropTypes.bool,
  };

  static defaultProps = {
    type: 'toggle',
    onChange: () => {},
  };

  static displayName = 'SelectableAccordionItem';

  state = { hovered: false };

  _onChange = event => {
    const { idx, type, open, onChange, disabled } = this.props;

    if (disabled) {
      return;
    }

    if (type === 'radio' && open) {
      return;
    }

    onChange(event, idx, !open);
  };

  _onMouseEnter = () => this.setState({ hovered: true });

  _onMouseLeave = () => this.setState({ hovered: false });

  _renderSelector() {
    const { type, open, disabled } = this.props;
    let selector = null;

    switch (type) {
      case TYPES.CHECKBOX:
        selector = (
          <Checkbox
            checked={open}
            onChange={this._onChange}
            disabled={disabled}
          />
        );
        break;
      case TYPES.RADIO:
        selector = (
          <Radio checked={open} onChange={this._onChange} disabled={disabled} />
        );
        break;
      case TYPES.TOGGLE:
        selector = (
          <ToggleSwitch
            checked={open}
            onChange={this._onChange}
            size="small"
            disabled={disabled}
          />
        );
        break;
      default:
        break;
    }
    return (
      <span
        className={classes.selectorToggle}
        onClick={e => e.stopPropagation()}
      >
        {selector}
      </span>
    );
  }

  _renderTitle() {
    const { title } = this.props;

    if (isString(title)) {
      return (
        <Text weight="normal" size="medium">
          {title}
        </Text>
      );
    } else {
      return title;
    }
  }

  _renderContent() {
    const { content } = this.props;

    return isString(content) ? (
      <Text className={classes.text} size="small" weight="thin">
        {content}
      </Text>
    ) : (
      content
    );
  }

  _renderSubtitle() {
    const { subtitle } = this.props;

    return isString(subtitle) ? (
      <Text ellipsis size="small" weight="thin">
        {subtitle}
      </Text>
    ) : (
      subtitle
    );
  }

  render() {
    const { hovered } = this.state;
    const { open, verticalPadding, disabled, content } = this.props;

    return (
      <WixStyleReactContext.Consumer>
        {({ newColorsBranding }) => (
          <div
            data-hook={dataHooks.item}
            data-state={open ? 'open' : 'collapsed'}
            data-disabled={disabled}
            className={st(classes.item, {
              hovered: !open && hovered,
              verticalPadding,
              disabled,
              newColorsBranding,
            })}
          >
            <div
              onMouseEnter={this._onMouseEnter}
              onMouseLeave={this._onMouseLeave}
              onClick={this._onChange}
              className={classes.selector}
            >
              {this._renderSelector()}
            </div>
            <div
              data-hook={dataHooks.itemHeader}
              onMouseEnter={this._onMouseEnter}
              onMouseLeave={this._onMouseLeave}
              onClick={this._onChange}
              className={classes.header}
            >
              {this._renderTitle()}
              {this._renderSubtitle()}
            </div>
            <div className={classes.content}>
              {content ? (
                <Collapse open={open}>
                  <div className={classes.inner}>{this._renderContent()}</div>
                </Collapse>
              ) : null}
              <div className={classes.divider}>
                <Divider />
              </div>
            </div>
          </div>
        )}
      </WixStyleReactContext.Consumer>
    );
  }
}
